import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from './api-config';
import {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginMethodResponse,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  UserDto
} from '../models/auth.models';

interface UpdateUserRequest {
  fullName: string;
  phoneNumber: string | null;
  preferredLocale: string;
  isActive: boolean;
  roles: string[];
}

const STORAGE_KEY = 'fms.auth';

interface StoredAuth {
  accessToken: string;
  expiresAt: string;
  user: UserDto;
  organizationName: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  hasLogo: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly state = signal<StoredAuth | null>(this.readStorage());

  readonly currentUser = computed(() => this.state()?.user ?? null);
  readonly currentOrganizationName = computed(() => this.state()?.organizationName ?? null);
  /** Per-org branding (Azure Boards #9236) — rides along on login/localStorage, same staleness trade-off
   * as roles/building grants: a branding change doesn't take effect until the affected user logs in again. */
  readonly currentBranding = computed(() => {
    const s = this.state();
    return s ? { primaryColor: s.primaryColor, secondaryColor: s.secondaryColor, hasLogo: s.hasLogo } : null;
  });
  readonly isAuthenticated = computed(() => {
    const s = this.state();
    return !!s && new Date(s.expiresAt).getTime() > Date.now();
  });
  readonly roles = computed(() => this.state()?.user.roles ?? []);

  constructor(private http: HttpClient) {}

  get accessToken(): string | null {
    return this.state()?.accessToken ?? null;
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${API_BASE_URL}/auth/login`, request).pipe(tap((response) => this.storeSession(response)));
  }

  /** docs/design/08_auth_account_lifecycle_and_sso.md §1.3 - given an email, tells the login page whether to
   * reveal the password field or redirect the browser straight to the org's IdP. */
  getLoginMethod(email: string): Observable<LoginMethodResponse> {
    return this.http.post<LoginMethodResponse>(`${API_BASE_URL}/auth/login-method`, { email });
  }

  /** Called by SsoCallbackComponent once the API's OIDC callback has redirected back with a full
   * LoginResponse payload - stores the session exactly as a password login would, so nothing downstream
   * of login needs to know which path established it. */
  completeSsoLogin(response: LoginResponse): void {
    this.storeSession(response);
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/auth/forgot-password`, { email } satisfies ForgotPasswordRequest);
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${API_BASE_URL}/auth/reset-password`, { token, newPassword } satisfies ResetPasswordRequest);
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.patch<void>(`${API_BASE_URL}/users/me/password`, { currentPassword, newPassword } satisfies ChangePasswordRequest);
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.state.set(null);
  }

  private storeSession(response: LoginResponse): void {
    const stored: StoredAuth = {
      accessToken: response.accessToken,
      expiresAt: response.expiresAt,
      user: response.user,
      organizationName: response.organizationName,
      primaryColor: response.primaryColor,
      secondaryColor: response.secondaryColor,
      hasLogo: response.hasLogo
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    this.state.set(stored);
  }

  /** Persists a new preferred locale via PATCH /users/me, round-tripping this user's other current fields unchanged. */
  updatePreferredLocale(locale: string): Observable<UserDto> {
    const current = this.state();
    const user = current?.user;
    const body: UpdateUserRequest = {
      fullName: user?.fullName ?? '',
      phoneNumber: user?.phoneNumber ?? null,
      preferredLocale: locale,
      isActive: user?.isActive ?? true,
      roles: user?.roles ?? []
    };
    return this.http.patch<UserDto>(`${API_BASE_URL}/users/me`, body).pipe(
      tap((updated) => {
        if (current) {
          const stored: StoredAuth = { ...current, user: updated };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
          this.state.set(stored);
        }
      })
    );
  }

  private readStorage(): StoredAuth | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StoredAuth;
    } catch {
      return null;
    }
  }
}
